/**
 * Dispatch Planning tab (Admin > Retailer Orders > Daily Requirement).
 *
 * UX rules (v2, per user feedback):
 *  - Products are grouped by category (Fruits/Vegetables/Leafy/Exotic/Sprouts/Other).
 *  - Retailer sub-rows are collapsed by default; user expands per product.
 *  - Variant input is an autocomplete: yesterday-dispatched variants show first,
 *    then all packaging variants matching the product's unit.
 *  - Numeric inputs (planned qty, MRP) accept blank so staff can clear zero and retype.
 *  - When planned_qty for a retailer < indent_qty, an "+ Add another variant" chip
 *    appears so staff can split one retailer's line across multiple variants
 *    (common when procurement lands in mixed pack sizes).
 *
 * Persist via POST /api/dispatch-plans/save. Retailer rows are stored as a flat
 * list; multiple entries with the same retailer_id represent the "add another
 * variant" case.
 */
import React, { useCallback, useEffect, useMemo, useState, useRef } from 'react';
import { toast } from 'sonner';
import { ChevronDown, ChevronRight, RefreshCw, Save, Search, Truck, Plus, Trash2, X } from 'lucide-react';
import { Button } from './ui/button';
import api from '../utils/api';

const categoryOrder = { Vegetables: 1, Leafy: 2, Fruits: 3, Exotic: 4, Sprouts: 5, Flowers: 6, Other: 99 };
const categoryPill = {
  Vegetables: 'bg-emerald-50 text-emerald-700 border-emerald-200',
  Leafy: 'bg-lime-50 text-lime-700 border-lime-200',
  Fruits: 'bg-orange-50 text-orange-700 border-orange-200',
  Exotic: 'bg-fuchsia-50 text-fuchsia-700 border-fuchsia-200',
  Sprouts: 'bg-amber-50 text-amber-700 border-amber-200',
  Flowers: 'bg-pink-50 text-pink-700 border-pink-200',
  Other: 'bg-gray-50 text-gray-700 border-gray-200',
};

// Extract weight in grams from a variant name.
// Handles "500 g", "500gm", "500 gm Packet", "1 kg", "1kg", "250" (bare number treated as grams).
// Returns null if no weight can be parsed.
function extractWeightGm(name) {
  if (!name) return null;
  const m = String(name).match(/(\d+(?:\.\d+)?)\s*(kg|gm|g)?/i);
  if (!m) return null;
  const num = parseFloat(m[1]);
  if (!isFinite(num) || num <= 0) return null;
  const unit = (m[2] || 'g').toLowerCase();
  return unit === 'kg' ? num * 1000 : num;
}

// Given a reference (variant, mrp) pair, compute the proportional MRP for a
// new variant name. Rounds UP to the next whole number per user requirement
// (23.2 -> 24). Returns null if either weight can't be parsed.
function computeRatioMrp(newVariantName, refVariantName, refMrp) {
  const refWt = extractWeightGm(refVariantName);
  const newWt = extractWeightGm(newVariantName);
  const refMrpNum = Number(refMrp);
  if (!refWt || !newWt || !isFinite(refMrpNum) || refMrpNum <= 0) return null;
  const raw = (newWt * refMrpNum) / refWt;
  if (!isFinite(raw) || raw <= 0) return null;
  return Math.ceil(raw);
}

// Small autocomplete combobox for the Variant column. Prioritizes recent variants
// (last-dispatched yesterday) at the top; matches the Stickers & MRP feel.
function VariantCombobox({ value, options = [], recent = [], onChange, testId }) {
  const [open, setOpen] = useState(false);
  // `text` is the inner field value shown to the user. Parent state is only
  // updated when the user COMMITS a value picked from the list - we never
  // save a typed-but-unmatched variant since every saved line needs a real
  // variant_id (packaging catalog FK). See handleCommit below.
  const [text, setText] = useState(value || '');
  const wrapRef = useRef(null);

  useEffect(() => { setText(value || ''); }, [value]);

  useEffect(() => {
    if (!open) return;
    const onClick = (e) => {
      if (wrapRef.current && !wrapRef.current.contains(e.target)) {
        // Clicking away = treat like blur: either match-and-commit, or revert.
        handleCommit(text, { fromBlur: true });
        setOpen(false);
      }
    };
    document.addEventListener('mousedown', onClick);
    return () => document.removeEventListener('mousedown', onClick);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, text]);

  // Build the ordered option list once, with recent variants pinned at the
  // top. `options` is the full {id, name, size, unit} catalog from the API.
  const orderedOptions = useMemo(() => {
    const byLower = new Map();
    (options || []).forEach(o => {
      const key = (o?.name || '').toLowerCase();
      if (key && !byLower.has(key)) byLower.set(key, o);
    });
    const recentOpts = [];
    const seen = new Set();
    (recent || []).forEach(r => {
      const key = (r || '').toLowerCase();
      const opt = byLower.get(key);
      if (opt && !seen.has(opt.id)) {
        recentOpts.push(opt);
        seen.add(opt.id);
      }
    });
    const restOpts = [];
    (options || []).forEach(o => {
      if (!o?.id || seen.has(o.id)) return;
      restOpts.push(o);
      seen.add(o.id);
    });
    return [...recentOpts, ...restOpts];
  }, [options, recent]);

  const suggestions = useMemo(() => {
    const q = (text || '').trim().toLowerCase();
    if (!q) return orderedOptions.slice(0, 25);
    return orderedOptions.filter(o => (o.name || '').toLowerCase().includes(q)).slice(0, 25);
  }, [text, orderedOptions]);

  // Commit a value only if it exactly matches a packaging in the catalog
  // (case-insensitive). Empty string is also commit-able (clears the field).
  const handleCommit = (candidate, { fromBlur = false } = {}) => {
    const trimmed = (candidate || '').trim();
    if (trimmed === '') {
      // Blank is allowed (clears variant + variant_id). Only fire onChange if
      // the current committed value isn't already empty, so a no-op blur on an
      // already-empty field doesn't dirty the plan.
      setText('');
      if ((value || '').trim() !== '') {
        onChange('', { committed: true, variant_id: '' });
      }
      setOpen(false);
      return;
    }
    const match = (options || []).find(o => (o.name || '').toLowerCase() === trimmed.toLowerCase());
    if (!match) {
      // Not a known pack: show error, revert, keep the dropdown open ONLY on
      // explicit Enter / click events so a stray blur doesn't trap focus.
      if (fromBlur) {
        toast.error('Please select a pack size from the list');
        setText(value || '');
        setOpen(false);
      } else {
        setText(value || '');
        setOpen(true);
      }
      return;
    }
    setText(match.name);
    // Skip the onChange when the match is already the committed value AND the
    // caller didn't change the text (prevents a blur on an unchanged retailer
    // variant from blanking the retailer MRP via updateRetailerField).
    const sameAsValue = (value || '').toLowerCase() === match.name.toLowerCase();
    if (!sameAsValue) {
      onChange(match.name, { committed: true, variant_id: match.id });
    }
    setOpen(false);
  };

  return (
    <div className="relative" ref={wrapRef}>
      <input
        type="text"
        value={text}
        onFocus={() => setOpen(true)}
        onChange={(e) => { setText(e.target.value); setOpen(true); }}
        onBlur={() => { handleCommit(text, { fromBlur: true }); }}
        onKeyDown={(e) => {
          if (e.key === 'Escape') { setText(value || ''); setOpen(false); }
          if (e.key === 'Enter') { e.preventDefault(); handleCommit(text, { fromBlur: true }); }
        }}
        placeholder="Pick a pack size"
        className="w-full h-7 px-2 border rounded text-xs focus:outline-none focus:ring-1 focus:ring-emerald-500"
        data-testid={testId}
      />
      {open && suggestions.length > 0 && (
        <div className="absolute z-20 mt-0.5 left-0 right-0 bg-white border rounded-md shadow-lg max-h-52 overflow-y-auto text-xs">
          {suggestions.map((opt, i) => {
            const isRecent = (recent || []).some(r => (r || '').toLowerCase() === (opt.name || '').toLowerCase());
            return (
              <button
                type="button"
                key={`${opt.id}-${i}`}
                className={`w-full text-left px-2 py-1 hover:bg-emerald-50 flex items-center justify-between ${isRecent ? 'bg-yellow-50' : ''}`}
                onMouseDown={(e) => { e.preventDefault(); handleCommit(opt.name); }}
              >
                <span>{opt.name}</span>
                {isRecent && <span className="text-[9px] text-yellow-700 font-medium">recent</span>}
              </button>
            );
          })}
        </div>
      )}
      {open && suggestions.length === 0 && (
        <div className="absolute z-20 mt-0.5 left-0 right-0 bg-white border rounded-md shadow-lg text-xs">
          <div className="px-2 py-2 text-gray-500 italic">No pack size matches "{text}"</div>
        </div>
      )}
    </div>
  );
}

// Numeric input that allows the value to be blanked out. State stays as string
// while user types; parent gets the numeric-or-blank string via onChange.
// Selects existing content on focus so typing replaces the pre-filled "0"
// (fixes "020" prefix issue).
// Treats an incoming value of 0 as an empty display so staff don't have to
// select-and-delete the placeholder zero on every field on phones.
function BlankableNumberInput({ value, onChange, className, testId, step = '0.1', min = '0' }) {
  const isBlankish = (v) => v === '' || v == null || Number(v) === 0;
  const [text, setText] = useState(isBlankish(value) ? '' : String(value));
  useEffect(() => {
    setText(isBlankish(value) ? '' : String(value));
  }, [value]);
  const stripLeadingZeros = (v) => {
    // "020" -> "20", "0.5" stays, "0" stays, "" stays
    if (!v) return v;
    if (/^0\d/.test(v)) return v.replace(/^0+/, '') || '0';
    return v;
  };
  return (
    <input
      type="text"
      inputMode="decimal"
      value={text}
      onFocus={(e) => { try { e.target.select(); } catch (_) { /* noop */ } }}
      onChange={(e) => {
        let v = e.target.value;
        // Allow empty or valid decimal
        if (v === '' || /^-?\d*\.?\d*$/.test(v)) {
          v = stripLeadingZeros(v);
          setText(v);
          onChange(v);
        }
      }}
      onBlur={() => {
        // Normalize trailing dot to itself; leave blank as blank
        if (text === '' || text === '.') return;
      }}
      className={className}
      data-testid={testId}
      step={step}
      min={min}
    />
  );
}

export default function DispatchPlanning({ dailyReqDate }) {
  const [products, setProducts] = useState([]);
  // Keep an up-to-date ref so async callbacks (post-setState) can read latest.
  const productsRef = useRef([]);
  useEffect(() => { productsRef.current = products; }, [products]);
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [expanded, setExpanded] = useState({});
  const [collapsedCategories, setCollapsedCategories] = useState({});
  const [dirty, setDirty] = useState(false);
  const [search, setSearch] = useState('');
  // Fresh retailer_id -> category map. Overrides any stale retailer_category
  // embedded in the loaded plan payload, so profile updates apply live.
  const [categoryMap, setCategoryMap] = useState({});

  const refreshCategoryMap = useCallback(async () => {
    try {
      const res = await api.get('/api/dispatch-plans/retailer-categories');
      setCategoryMap(res.data || {});
    } catch (e) {
      // Silent - fall back to embedded retailer_category on the row
    }
  }, []);

  // Resolve a retailer's live category (prefers fresh map over embedded field).
  const catOf = useCallback(
    (row) => ((categoryMap[row?.retailer_id] || row?.retailer_category || '')).toLowerCase(),
    [categoryMap],
  );

  const load = useCallback(async () => {
    if (!dailyReqDate) return;
    setLoading(true);
    try {
      // Always refresh the retailer category map alongside the plan.
      const [planRes] = await Promise.all([
        api.get(`/api/dispatch-plans?date=${dailyReqDate}`),
        refreshCategoryMap(),
      ]);
      // Normalize each product so product-level Planned Qty and Surplus/Deficit
      // are derived from retailer allocations (product cell is read-only).
      const normalized = (planRes.data?.products || []).map(p => recomputeProduct(p));
      setProducts(normalized);
      setDirty(false);
      // Collapsed by default per user request
      setExpanded({});

      // Auto re-allocate any product whose indent qtys have drifted since
      // the plan was last saved (backend sets indent_changed = true).
      const driftTargets = normalized.filter(p => p.indent_changed && (p.variants || []).some(v => parseNum(v.planned_qty) > 0));
      if (driftTargets.length > 0) {
        const toastId = toast.loading(`Indent changed for ${driftTargets.length} product${driftTargets.length > 1 ? 's' : ''} - re-allocating...`);
        try {
          const results = await Promise.all(
            driftTargets.map(p => {
              const payload = buildAllocPayload(p);
              return api.post('/api/dispatch-plans/auto-allocate', payload)
                .then(res => ({ pid: p.product_id, retailers: res.data?.retailers || [], ok: true }))
                .catch(err => ({ pid: p.product_id, ok: false, err }));
            }),
          );
          const okMap = Object.fromEntries(results.filter(r => r.ok).map(r => [r.pid, r.retailers]));
          setProducts(prev => prev.map(p => (
            okMap[p.product_id]
              ? recomputeProduct({ ...p, retailers: okMap[p.product_id], indent_changed: false })
              : p
          )));
          setDirty(true);
          toast.success(`Re-allocated ${Object.keys(okMap).length} product${Object.keys(okMap).length !== 1 ? 's' : ''} after indent change`, { id: toastId });
        } catch (e) {
          toast.error('Auto re-allocation failed - click Auto-Allocate All to retry', { id: toastId });
        }
      }
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Failed to load dispatch plan');
    } finally {
      setLoading(false);
    }
  }, [dailyReqDate, refreshCategoryMap]);

  useEffect(() => { load(); }, [load]);

  // Keep the retailer category map fresh: on window focus and every 30s.
  // This makes a Gold -> Bronze profile change reflect within seconds even
  // when the plan itself is not reloaded (so in-progress edits stay).
  useEffect(() => {
    const onFocus = () => refreshCategoryMap();
    window.addEventListener('focus', onFocus);
    const intervalId = setInterval(refreshCategoryMap, 30000);
    return () => {
      window.removeEventListener('focus', onFocus);
      clearInterval(intervalId);
    };
  }, [refreshCategoryMap]);

  // -------- Editing helpers --------

  const parseNum = (v) => {
    if (v === '' || v == null) return 0;
    const n = parseFloat(v);
    return Number.isFinite(n) ? n : 0;
  };

  const recomputeProduct = (p) => {
    const rows = p.retailers || [];
    // Ensure a variants array exists (backfill from legacy default_* fields)
    let variants = Array.isArray(p.variants) && p.variants.length > 0
      ? p.variants
      : [{
          variant: p.default_variant || '',
          variant_id: '',
          mrp: p.default_mrp || 0,
          planned_qty: 0,
        }];
    // Planned Qty at PRODUCT level = sum of variants[i].planned_qty (new rule)
    const variantSum = variants.reduce((s, v) => s + parseNum(v.planned_qty), 0);
    const allocSum = rows.reduce((s, r) => s + parseNum(r.planned_qty), 0);
    const variantCounts = {};
    rows.forEach(r => {
      const key = r.variant || '-';
      variantCounts[key] = (variantCounts[key] || 0) + parseNum(r.planned_qty);
    });
    const mix = Object.entries(variantCounts)
      .filter(([v]) => v && v !== '-')
      .sort((a, b) => b[1] - a[1])
      .map(([v, c]) => `${v} x ${Number.isInteger(c) ? c : (+c.toFixed(2))}`)
      .join(', ');
    return {
      ...p,
      variants,
      variant_mix: mix,
      planned_total_qty: +variantSum.toFixed(3),
      allocated_qty: +allocSum.toFixed(3),
      surplus_deficit: +(variantSum - parseNum(p.indent_total_qty)).toFixed(3),
      unallocated_qty: +(variantSum - allocSum).toFixed(3),
    };
  };

  // -------- Product-level VARIANT helpers --------

  const updateProductVariant = (pid, index, field, val, opts = {}) => {
    setProducts(prev => prev.map(p => {
      if (p.product_id !== pid) return p;
      // Prevent duplicate variant names (guards on every keystroke so user
      // can't create a dup even mid-typing).
      if (field === 'variant' && val) {
        const dup = (p.variants || []).some((v, i) => i !== index && (v.variant || '').trim().toLowerCase() === String(val).trim().toLowerCase());
        if (dup) {
          if (opts.committed) toast.error(`Variant "${val}" is already added on this product.`);
          return p;
        }
      }
      let newVariants = (p.variants || []).map((v, i) => {
        if (i !== index) return v;
        // When the variant name is committed from the dropdown, VariantCombobox
        // passes { variant_id } alongside. Persist it so saved lines carry a
        // real packaging FK (removes the empty-id regression on Dozen/Piece).
        if (field === 'variant') {
          const next = { ...v, variant: val };
          if (opts && Object.prototype.hasOwnProperty.call(opts, 'variant_id')) {
            next.variant_id = opts.variant_id || '';
          }
          return next;
        }
        return { ...v, [field]: val };
      });

      // RULE 1: When user types planned_qty > 0 on the FIRST empty row and no
      // variant is set yet, auto-fill variant + MRP from yesterday's default
      // (backend already falls back to day-before-yesterday when needed).
      if (field === 'planned_qty' && parseNum(val) > 0 && index === 0) {
        const row = newVariants[0];
        const isEmptyRow = !(row.variant || '').trim() && (row.mrp === '' || row.mrp == null || Number(row.mrp) === 0);
        if (isEmptyRow && (p.default_variant || '').trim()) {
          newVariants = newVariants.map((v, i) => i === 0
            ? { ...v, variant: p.default_variant, variant_id: p.default_variant_id || '', mrp: p.default_mrp || v.mrp }
            : v
          );
        }
      }

      return recomputeProduct({ ...p, variants: newVariants });
    }));
    setDirty(true);

    // RULE 2: Ratio-based MRP auto-fill for additional variant rows. Only
    // fire when the variant name is COMMITTED (user picked from dropdown,
    // tabbed away, or pressed Enter). Firing per-keystroke would compute
    // ceil(3*25/400)=1 while the user is still typing "300+ gm", freeze the
    // field at 1, and never recover.
    //
    // MRP source is the FORMULA only (proportional to first earlier row that
    // has both variant + MRP). If the weight can't be parsed from either
    // variant name, we leave the MRP field blank so staff enters it manually.
    // No fallback to the Stickers & MRP subtab here - that is reserved for
    // the FIRST variant row (Rule 1) per user spec.
    if (field === 'variant' && val && opts.committed) {
      setProducts(prev => prev.map(p => {
        if (p.product_id !== pid) return p;
        const current = p.variants || [];
        const target = current[index];
        if (!target) return p;
        const targetEmpty = target.mrp === '' || target.mrp == null || Number(target.mrp) === 0;
        if (!targetEmpty) return p;

        // Reference = first earlier row with both variant and mrp
        const ref = current.find((v, i) => i < index && (v.variant || '').trim() && Number(v.mrp) > 0);
        if (!ref) return p;

        const ratio = computeRatioMrp(val, ref.variant, ref.mrp);
        if (ratio == null) return p; // parse failed -> leave blank

        const newVariants = current.map((v, i) => (i === index ? { ...v, mrp: ratio } : v));
        return recomputeProduct({ ...p, variants: newVariants });
      }));
    }
  };

  const addProductVariant = (pid) => {
    setProducts(prev => prev.map(p => {
      if (p.product_id !== pid) return p;
      const newVariants = [...(p.variants || []), { variant: '', variant_id: '', mrp: 0, planned_qty: 0 }];
      return recomputeProduct({ ...p, variants: newVariants });
    }));
    setDirty(true);
  };

  const removeProductVariant = (pid, index) => {
    setProducts(prev => prev.map(p => {
      if (p.product_id !== pid) return p;
      const current = p.variants || [];
      if (current.length <= 1) {
        toast.error('At least one variant row is required. Set qty to 0 instead of removing.');
        return p;
      }
      const newVariants = current.filter((_, i) => i !== index);
      return recomputeProduct({ ...p, variants: newVariants });
    }));
    setDirty(true);
  };

  // Build the auto-allocate request payload for a single product (also used
  // by the bulk "Auto-Allocate All" flow and the auto-reallocate-on-drift path).
  const buildAllocPayload = (p) => {
    const variants = (p.variants || []).filter(v => parseNum(v.planned_qty) > 0);
    if (!variants.length) return null;
    // Collect variant_indents per retailer from the primary row (the loader
    // puts the breakdown on is_primary=true rows only).
    const variantIndentsByRid = {};
    (p.retailers || []).forEach(r => {
      if (!r.retailer_id) return;
      if (variantIndentsByRid[r.retailer_id]) return;
      const vi = Array.isArray(r.variant_indents) ? r.variant_indents : [];
      if (vi.length > 0) {
        variantIndentsByRid[r.retailer_id] = vi
          .filter(x => parseNum(x?.qty) > 0)
          .map(x => ({
            variant_id: x?.variant_id || '',
            variant_name: x?.variant_name || '',
            qty: parseNum(x?.qty),
          }));
      }
    });
    const seen = new Set();
    const retailersDedup = [];
    (p.retailers || []).forEach(r => {
      if (seen.has(r.retailer_id)) return;
      seen.add(r.retailer_id);
      retailersDedup.push({
        retailer_id: r.retailer_id,
        retailer_name: r.retailer_name,
        indent_qty: parseNum(r.indent_qty),
        planned_qty: 0,
        variant_indents: variantIndentsByRid[r.retailer_id] || [],
      });
    });
    return {
      date: dailyReqDate,
      product_id: p.product_id,
      variants: variants.map(v => ({
        variant: v.variant || '',
        variant_id: v.variant_id || '',
        mrp: parseNum(v.mrp),
        planned_qty: parseNum(v.planned_qty),
      })),
      retailers: retailersDedup,
    };
  };

  // Run backend auto-allocation for this product and replace its retailer rows.
  const autoAllocateProduct = async (pid) => {
    const p = products.find(x => x.product_id === pid);
    if (!p) return;
    const payload = buildAllocPayload(p);
    if (!payload) {
      toast.error('Enter planned qty in at least one variant before allocating.', { position: 'top-center' });
      return;
    }
    try {
      const res = await api.post('/api/dispatch-plans/auto-allocate', payload);
      setProducts(prev => prev.map(x => (
        x.product_id === pid
          ? recomputeProduct({ ...x, retailers: res.data?.retailers || [] })
          : x
      )));
      setDirty(true);
      // Collapse the row after allocation so the list stays scannable on phones.
      setExpanded(prev => ({ ...prev, [pid]: false }));
      toast.success(
        `Allocated ${res.data?.total_allocated || 0} across ${res.data?.retailer_count || 0} retailers`,
        { position: 'top-center' },
      );
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Auto-allocation failed', { position: 'top-center' });
    }
  };

  // Bulk auto-allocate: runs the allocator for every product that has variant
  // qtys entered. One click from the header. Silent for products without
  // variant qtys yet.
  const autoAllocateAll = async () => {
    const targets = products
      .map(p => ({ pid: p.product_id, payload: buildAllocPayload(p) }))
      .filter(x => !!x.payload);
    if (targets.length === 0) {
      toast.error('Enter Planned Qty in at least one product variant first.', { position: 'top-center' });
      return;
    }
    const toastId = toast.loading(`Allocating ${targets.length} product${targets.length > 1 ? 's' : ''}...`, { position: 'top-center' });
    try {
      const results = await Promise.all(
        targets.map(t => api.post('/api/dispatch-plans/auto-allocate', t.payload)
          .then(res => ({ pid: t.pid, retailers: res.data?.retailers || [], ok: true }))
          .catch(err => ({ pid: t.pid, ok: false, err }))),
      );
      const okMap = Object.fromEntries(results.filter(r => r.ok).map(r => [r.pid, r.retailers]));
      setProducts(prev => prev.map(p => (
        okMap[p.product_id]
          ? recomputeProduct({ ...p, retailers: okMap[p.product_id] })
          : p
      )));
      setDirty(true);
      // Collapse every product that got allocated so the list is scannable again.
      setExpanded(prev => {
        const next = { ...prev };
        Object.keys(okMap).forEach(pid => { next[pid] = false; });
        return next;
      });
      const failed = results.filter(r => !r.ok).length;
      if (failed > 0) {
        toast.error(`${failed} product${failed > 1 ? 's' : ''} failed to allocate`, { id: toastId, position: 'top-center' });
      } else {
        toast.success(`Auto-allocated ${targets.length} product${targets.length > 1 ? 's' : ''}`, { id: toastId, position: 'top-center' });
      }
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Bulk allocation failed', { id: toastId, position: 'top-center' });
    }
  };

  // Cascade Default Variant / Default MRP down to every retailer sub-line for
  // this product. Per user rule: when a retailer's variant differs from the
  // default, that retailer's MRP is left blank (staff must fill it manually).
  const updateProductDefault = (pid, field, val) => {
    setProducts(prev => prev.map(p => {
      if (p.product_id !== pid) return p;
      if (field === 'default_variant') {
        const newRetailers = (p.retailers || []).map(r => {
          if (!r.variant || r.variant === p.default_variant) {
            // was blank or was matching the previous default -> update to new default
            return { ...r, variant: val, mrp: p.default_mrp };
          }
          // different variant retained -> keep as is (staff picked it deliberately)
          return r;
        });
        return recomputeProduct({ ...p, default_variant: val, retailers: newRetailers });
      }
      if (field === 'default_mrp') {
        const newRetailers = (p.retailers || []).map(r => (
          (r.variant || '') === (p.default_variant || '') ? { ...r, mrp: val } : r
        ));
        return recomputeProduct({ ...p, default_mrp: val, retailers: newRetailers });
      }
      return { ...p, [field]: val };
    }));
    setDirty(true);
  };

  const updateRetailerField = (pid, lineId, field, val, opts = {}) => {
    setProducts(prev => prev.map(p => {
      if (p.product_id !== pid) return p;
      const newRetailers = (p.retailers || []).map(r => {
        if (r.line_id !== lineId) return r;
        // If staff changes the retailer's variant to something different from
        // the product default, blank out the retailer's MRP so they must fill it.
        if (field === 'variant') {
          // Capture variant_id when the combobox commits a packaging pick.
          const vid = (opts && Object.prototype.hasOwnProperty.call(opts, 'variant_id'))
            ? (opts.variant_id || '')
            : r.variant_id;
          if (val && val !== (p.default_variant || '')) {
            return { ...r, variant: val, variant_id: vid, mrp: '' };
          }
          // Variant matches product default (or is blank) -> auto-fill MRP from default
          return { ...r, variant: val, variant_id: vid, mrp: p.default_mrp };
        }
        return { ...r, [field]: val };
      });
      return recomputeProduct({ ...p, retailers: newRetailers });
    }));
    setDirty(true);
  };

  const addAnotherVariant = (pid, retailerId) => {
    setProducts(prev => prev.map(p => {
      if (p.product_id !== pid) return p;
      const rows = p.retailers || [];
      // Find retailer's lines to get the name and the next sub-index
      const rLines = rows.filter(r => r.retailer_id === retailerId);
      if (!rLines.length) return p;
      const retailerName = rLines[0].retailer_name;
      const retailerCategory = rLines[0].retailer_category || '';
      const nextIdx = rLines.length;
      const newLine = {
        line_id: `${retailerId}-${nextIdx}-${Date.now()}`,
        retailer_id: retailerId,
        retailer_name: retailerName,
        retailer_category: retailerCategory,
        indent_qty: 0,        // only the first (primary) line carries the indent qty
        is_primary: false,
        planned_qty: '',
        variant: '',
        variant_id: '',
        mrp: '',
      };
      // Insert right after the retailer's last existing line so sub-lines stay grouped
      const lastIdx = rows.map(r => r.retailer_id).lastIndexOf(retailerId);
      const newRows = [...rows.slice(0, lastIdx + 1), newLine, ...rows.slice(lastIdx + 1)];
      return recomputeProduct({ ...p, retailers: newRows });
    }));
    setDirty(true);
  };

  const removeRetailerLine = (pid, lineId) => {
    // Gold-tier floor is enforced only at save time (see backend).
    setProducts(prev => prev.map(p => {
      if (p.product_id !== pid) return p;
      const newRetailers = (p.retailers || []).filter(r => r.line_id !== lineId);
      return recomputeProduct({ ...p, retailers: newRetailers });
    }));
    setDirty(true);
  };

  // -------- Save --------

  const save = async () => {
    setSaving(true);
    try {
      // For every product with variants but NO retailer allocations yet,
      // run the auto-allocate endpoint before saving so retailer rows get
      // filled in without staff having to click Auto-Allocate for each product.
      const needAlloc = products.filter(p => {
        const variantPool = (p.variants || []).reduce((s, v) => s + parseNum(v.planned_qty), 0);
        const alloc = (p.retailers || []).reduce((s, r) => s + parseNum(r.planned_qty), 0);
        return variantPool > 0 && alloc <= 0;
      });

      const allocByPid = {};
      if (needAlloc.length > 0) {
        // Use the same payload builder as the Auto-Allocate buttons so the
        // per-retailer variant_indents breakdown is always sent. Without it the
        // allocator treats every retailer as 'any size' and piles a multi-variant
        // order onto the first variant (e.g. 8 x 50 gm instead of 4 x 50 gm + 4 x 100 gm).
        const results = await Promise.all(needAlloc.map(p => (
          api.post('/api/dispatch-plans/auto-allocate', buildAllocPayload(p))
            .then(res => ({ pid: p.product_id, retailers: res.data?.retailers || [] }))
        )));
        results.forEach(r => { allocByPid[r.pid] = r.retailers; });

        // Reflect the allocation in local state (so UI updates too)
        setProducts(prev => prev.map(p => (
          allocByPid[p.product_id]
            ? recomputeProduct({ ...p, retailers: allocByPid[p.product_id] })
            : p
        )));
      }

      const payload = {
        date: dailyReqDate,
        products: products.map(p => {
          const retailers = allocByPid[p.product_id] || p.retailers || [];
          return {
            product_id: p.product_id,
            product_name: p.product_name,
            category: p.category,
            unit: p.unit,
            planned_total_qty: parseNum(p.planned_total_qty),
            default_variant: p.default_variant || '',
            default_mrp: parseNum(p.default_mrp),
            not_available: !!p.not_available,
            variants: (p.variants || []).map(v => ({
              variant: v.variant || '',
              variant_id: v.variant_id || '',
              mrp: parseNum(v.mrp),
              planned_qty: parseNum(v.planned_qty),
            })),
            retailers: retailers.map(r => ({
              retailer_id: r.retailer_id,
              retailer_name: r.retailer_name,
              indent_qty: parseNum(r.indent_qty),
              planned_qty: parseNum(r.planned_qty),
              variant: r.variant || '',
              variant_id: r.variant_id || '',
              mrp: parseNum(r.mrp),
            })),
          };
        }),
      };
      const res = await api.post('/api/dispatch-plans/save', payload);
      const autoAllocMsg = needAlloc.length ? ` (auto-allocated ${needAlloc.length})` : '';
      toast.success(`Saved ${res.data?.saved || 0} product plans${autoAllocMsg}`);
      setDirty(false);
    } catch (e) {
      const detail = e.response?.data?.detail;
      const msg = typeof detail === 'object' ? detail.message : (detail || 'Failed to save dispatch plan');
      toast.error(msg);
    } finally {
      setSaving(false);
    }
  };

  // -------- Filter + group --------

  const grouped = useMemo(() => {
    const q = search.trim().toLowerCase();
    const rows = q ? products.filter(p => (p.product_name || '').toLowerCase().includes(q)) : products;
    const g = {};
    rows.forEach(p => {
      const cat = p.category || 'Other';
      if (!g[cat]) g[cat] = [];
      g[cat].push(p);
    });
    const sorted = Object.keys(g).sort((a, b) => (categoryOrder[a] || 99) - (categoryOrder[b] || 99));
    return { groups: g, sortedCategories: sorted, total: rows.length };
  }, [products, search]);

  const toggleExpand = (pid) => setExpanded(prev => ({ ...prev, [pid]: !prev[pid] }));

  // Toggle the "Not Available" flag on a product. When true, the product is
  // hidden from the dispatch pop-up for the plan date. This is persisted on
  // the dispatch_plans document and enforced server-side by
  // /api/dispatch-plans/for-retailer (which excludes not-available product ids).
  const toggleNotAvailable = (pid) => {
    setProducts(prev => prev.map(p => (
      p.product_id === pid ? { ...p, not_available: !p.not_available } : p
    )));
    setDirty(true);
  };

  if (!dailyReqDate) {
    return (
      <div className="text-center py-12 text-gray-500">
        <Truck size={48} className="mx-auto mb-4 opacity-30" />
        <p>Select a date to plan dispatches.</p>
      </div>
    );
  }

  return (
    <div data-testid="dispatch-planning-tab" className="space-y-3">
      {/* Toolbar - centered so Save / Auto-Allocate stay above the fold on phones (no horizontal scroll) */}
      <div className="flex flex-wrap items-center justify-center gap-2 pb-2 border-b">
        <div className="flex items-center gap-2">
          <div className="relative">
            <input
              type="text"
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              placeholder="Search product..."
              data-testid="dispatch-planning-search"
              className="h-8 pl-7 pr-6 text-xs border rounded-md focus:outline-none focus:ring-1 focus:ring-emerald-500 w-56"
            />
            <Search size={12} className="absolute left-2 top-1/2 -translate-y-1/2 text-gray-400" />
            {search && (
              <button
                type="button"
                onClick={() => setSearch('')}
                className="absolute right-1 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-600 text-xs px-1"
                title="Clear"
              >×</button>
            )}
          </div>
          <span className="text-xs text-gray-500">{grouped.total} products for {dailyReqDate}</span>
        </div>
        <div className="flex items-center gap-2">
          <Button
            size="sm"
            variant="outline"
            onClick={autoAllocateAll}
            disabled={loading || saving}
            className="text-emerald-700 border-emerald-300 hover:bg-emerald-50"
            data-testid="dispatch-planning-auto-allocate-all"
            title="Run auto-allocation for every product that has Planned Qty set"
          >
            <RefreshCw size={12} className="mr-1" /> Auto-Allocate All
          </Button>
          <Button size="sm" variant="outline" onClick={load} disabled={loading} data-testid="dispatch-planning-refresh">
            <RefreshCw size={12} className={`mr-1 ${loading ? 'animate-spin' : ''}`} /> Refresh
          </Button>
          <Button
            size="sm"
            onClick={save}
            disabled={saving || !dirty}
            className="bg-[#14532D] hover:bg-[#0f3f22]"
            data-testid="dispatch-planning-save"
          >
            <Save size={12} className="mr-1" /> {saving ? 'Saving...' : dirty ? 'Save Changes' : 'Saved'}
          </Button>
        </div>
      </div>

      {loading ? (
        <div className="flex items-center justify-center h-40">
          <div className="animate-spin rounded-full h-6 w-6 border-b-2 border-[#14532D]" />
        </div>
      ) : products.length === 0 ? (
        <div className="text-center py-12 text-gray-500">
          <Truck size={48} className="mx-auto mb-4 opacity-30" />
          <p>No indents found for {dailyReqDate}. Create indents first.</p>
        </div>
      ) : (
        <div className="space-y-4">
          {grouped.sortedCategories.map(cat => {
            const items = grouped.groups[cat];
            const isCatCollapsed = !!collapsedCategories[cat];
            return (
              <div key={cat} className="border rounded-lg overflow-hidden">
                <button
                  type="button"
                  onClick={() => setCollapsedCategories(prev => ({ ...prev, [cat]: !prev[cat] }))}
                  className={`w-full px-3 py-2 text-xs font-semibold border-b flex items-center justify-between ${categoryPill[cat] || categoryPill.Other}`}
                  data-testid={`category-toggle-${cat}`}
                >
                  <span className="flex items-center gap-1.5">
                    {isCatCollapsed ? <ChevronRight size={14} /> : <ChevronDown size={14} />}
                    {cat} <span className="opacity-70 ml-1">({items.length})</span>
                  </span>
                  <span className="text-[10px] opacity-80">
                    {isCatCollapsed ? 'Click to expand' : 'Click to collapse'}
                  </span>
                </button>
                {!isCatCollapsed && (
                <div className="overflow-x-auto">
                  <table className="w-full text-sm">
                    <thead className="bg-gray-50 text-xs text-gray-600">
                      <tr>
                        <th className="p-2 text-left w-10">#</th>
                        <th className="p-2 text-left w-8"></th>
                        <th className="p-2 text-left">Product Name</th>
                        <th className="p-2 text-left">Variant Mix</th>
                        <th className="p-2 text-center w-24">Indent Total</th>
                        <th className="p-2 text-center w-28">Planned Qty</th>
                        <th className="p-2 text-center w-40">Variants / MRP</th>
                        <th className="p-2 text-center w-32">Surplus / Deficit</th>
                        <th
                          className="p-2 text-center w-24"
                          title="Tick to hide this product from the dispatch pop-up for this date"
                        >
                          Not Available
                        </th>
                      </tr>
                    </thead>
                    <tbody>
                      {items.map((p, idx) => {
                        const sd = Number(p.surplus_deficit || 0);
                        const sdColor = sd > 0.001
                          ? 'bg-emerald-100 text-emerald-700 border border-emerald-300'
                          : sd < -0.001
                            ? 'bg-red-100 text-red-700 border border-red-300'
                            : 'bg-gray-100 text-gray-600 border border-gray-200';
                        const sdLabel = sd > 0.001
                          ? `+${sd.toFixed(2)} surplus`
                          : sd < -0.001
                            ? `${sd.toFixed(2)} deficit`
                            : 'Balanced';
                        const isOpen = !!expanded[p.product_id];

                        // Precompute sum of planned qty per retailer_id so
                        // "+ Add another variant" only shows on the last row of
                        // a retailer when their total planned qty < indent qty
                        const perRetailerSum = {};
                        const retailerLastLineId = {};
                        const retailerIndent = {};
                        (p.retailers || []).forEach(r => {
                          perRetailerSum[r.retailer_id] = (perRetailerSum[r.retailer_id] || 0) + parseNum(r.planned_qty);
                          if (r.is_primary) retailerIndent[r.retailer_id] = parseNum(r.indent_qty);
                          retailerLastLineId[r.retailer_id] = r.line_id;
                        });

                        return (
                          <React.Fragment key={p.product_id}>
                            <tr
                              className={`border-b hover:bg-gray-50 cursor-pointer ${p.not_available ? 'opacity-50 bg-red-50/40' : ''}`}
                              onClick={() => toggleExpand(p.product_id)}
                              data-testid={`dispatch-planning-row-${p.product_id}`}
                            >
                              <td className="p-2 text-xs text-gray-500">{idx + 1}</td>
                              <td className="p-2">
                                {isOpen ? <ChevronDown size={14} /> : <ChevronRight size={14} />}
                              </td>
                              <td className="p-2 font-medium">
                                {p.product_name}
                                {p.not_available && (
                                  <span
                                    className="ml-2 inline-block px-1.5 py-0.5 rounded text-[10px] font-semibold bg-red-100 text-red-700 border border-red-300 align-middle"
                                    data-testid={`not-available-tag-${p.product_id}`}
                                  >
                                    Not Available
                                  </span>
                                )}
                              </td>
                              <td className="p-2 text-xs text-gray-600">{p.variant_mix || '-'}</td>
                              <td className="p-2 text-center font-semibold">{p.indent_total_qty}</td>
                              <td className="p-2 text-center" onClick={e => e.stopPropagation()}>
                                {/* Product-level Planned Qty is derived from the sum of
                                    retailer allocations below - shown as a static number so
                                    staff always edit at the retailer row. */}
                                <span
                                  className="inline-block w-20 h-8 px-1 leading-8 text-center border rounded bg-gray-50 text-sm text-gray-700"
                                  data-testid={`planned-total-${p.product_id}`}
                                  title="Sum of retailer Planned Qty (auto-updated)"
                                >
                                  {Number(p.planned_total_qty) > 0
                                    ? (Number.isInteger(Number(p.planned_total_qty))
                                        ? Number(p.planned_total_qty)
                                        : Number(p.planned_total_qty).toFixed(2))
                                    : ''}
                                </span>
                              </td>
                              <td className="p-2" onClick={e => e.stopPropagation()}>
                                <div className="flex items-center justify-center gap-2">
                                  <span className="text-xs text-gray-500" data-testid={`variants-summary-${p.product_id}`}>
                                    {(p.variants || []).length} variant{(p.variants || []).length === 1 ? '' : 's'}
                                  </span>
                                  <button
                                    type="button"
                                    onClick={() => autoAllocateProduct(p.product_id)}
                                    className="px-2 py-1 rounded border text-[10px] font-semibold text-emerald-700 border-emerald-300 hover:bg-emerald-50"
                                    data-testid={`auto-allocate-${p.product_id}`}
                                    title="Distribute planned qty to retailers using the priority rules"
                                  >
                                    Auto-Allocate
                                  </button>
                                </div>
                              </td>
                              <td className="p-2 text-center">
                                <span className={`inline-block px-2 py-0.5 rounded text-[11px] font-medium ${sdColor}`}>
                                  {sdLabel}
                                </span>
                              </td>
                              <td className="p-2 text-center" onClick={e => e.stopPropagation()}>
                                <input
                                  type="checkbox"
                                  checked={!!p.not_available}
                                  onChange={() => toggleNotAvailable(p.product_id)}
                                  className="w-4 h-4 accent-red-600 cursor-pointer"
                                  title="Tick to hide this product from the dispatch pop-up"
                                  data-testid={`not-available-checkbox-${p.product_id}`}
                                />
                              </td>
                            </tr>
                            {isOpen && (
                              <tr className="bg-blue-50/40">
                                <td colSpan={9} className="p-0">
                                  {/* Product-level VARIANT editor: enter planned qty + variant + MRP.
                                      "Add another variant" creates additional rows. Wrapped in
                                      max-w-xl so the box stays compact on desktop while still
                                      fitting inside a phone viewport. */}
                                  <div className="p-1.5 border-b bg-amber-50/40">
                                    <div className="max-w-xl">
                                    <div className="flex items-center justify-between mb-1 px-1">
                                      <span className="text-[10px] font-semibold text-amber-800 uppercase tracking-wide">
                                        Product Pool - Qty, Variant, MRP
                                      </span>
                                      <span className="text-[10px] text-gray-500">
                                        Pool: <b>{p.planned_total_qty || 0}</b>
                                        {p.unallocated_qty > 0.001 ? ` - unallocated: ${p.unallocated_qty}` : ''}
                                      </span>
                                    </div>
                                    <table className="w-full text-xs">
                                      <thead className="bg-amber-100/70 text-amber-900">
                                        <tr>
                                          <th className="p-1 text-center w-20">Qty</th>
                                          <th className="p-1 text-left">Variant</th>
                                          <th className="p-1 text-center w-20">MRP (Rs)</th>
                                          <th className="p-1 w-8"></th>
                                        </tr>
                                      </thead>
                                      <tbody>
                                        {(p.variants || []).map((v, vi) => (
                                          <tr key={`v-${p.product_id}-${vi}`} className="border-b border-amber-200/60">
                                            <td className="p-1 text-center">
                                              <BlankableNumberInput
                                                value={v.planned_qty}
                                                onChange={(val) => updateProductVariant(p.product_id, vi, 'planned_qty', val)}
                                                className="w-16 h-7 px-1 text-center border rounded text-xs focus:outline-none focus:ring-1 focus:ring-amber-500"
                                                testId={`variant-qty-${p.product_id}-${vi}`}
                                                step="0.1"
                                              />
                                            </td>
                                            <td className="p-1">
                                              <VariantCombobox
                                                value={v.variant}
                                                options={p.variant_options || []}
                                                recent={p.recent_variants || []}
                                                onChange={(val, opts) => updateProductVariant(p.product_id, vi, 'variant', val, opts)}
                                                testId={`variant-name-${p.product_id}-${vi}`}
                                              />
                                            </td>
                                            <td className="p-1 text-center">
                                              <BlankableNumberInput
                                                value={v.mrp}
                                                onChange={(val) => updateProductVariant(p.product_id, vi, 'mrp', val)}
                                                className="w-16 h-7 px-1 text-center border rounded text-xs focus:outline-none focus:ring-1 focus:ring-amber-500"
                                                testId={`variant-mrp-${p.product_id}-${vi}`}
                                                step="0.5"
                                              />
                                            </td>
                                            <td className="p-1 text-center">
                                              {(p.variants || []).length > 1 && (
                                                <button
                                                  type="button"
                                                  onClick={() => removeProductVariant(p.product_id, vi)}
                                                  className="text-red-500 hover:text-red-700 text-xs"
                                                  title="Remove this variant"
                                                  data-testid={`remove-variant-${p.product_id}-${vi}`}
                                                >
                                                  <X size={12} />
                                                </button>
                                              )}
                                            </td>
                                          </tr>
                                        ))}
                                        <tr>
                                          <td colSpan={4} className="p-1">
                                            <button
                                              type="button"
                                              onClick={() => addProductVariant(p.product_id)}
                                              className="text-[11px] text-amber-700 hover:text-amber-900 font-medium"
                                              data-testid={`add-variant-${p.product_id}`}
                                            >
                                              + Add another variant
                                            </button>
                                          </td>
                                        </tr>
                                      </tbody>
                                    </table>
                                    </div>
                                  </div>

                                  <table className="w-full text-xs">
                                    <thead className="bg-blue-50 text-blue-700">
                                      <tr>
                                        <th className="p-2 text-left w-8"></th>
                                        <th className="p-2 text-left">Retailer</th>
                                        <th className="p-2 text-center w-24">Indent Qty</th>
                                        <th className="p-2 text-center w-28">Planned Qty</th>
                                        <th className="p-2 text-left w-48">Variant</th>
                                        <th className="p-2 text-center w-24">MRP (₹)</th>
                                        <th className="p-2 w-8"></th>
                                      </tr>
                                    </thead>
                                    <tbody>
                                      {(p.retailers || []).map((r, ridx) => {
                                        const canAdd = !r.is_primary
                                          ? false
                                          : (perRetailerSum[r.retailer_id] || 0) < (retailerIndent[r.retailer_id] || 0) - 0.001;
                                        // Show add button on the retailer's LAST sub-line only
                                        const isLastLineForRetailer = retailerLastLineId[r.retailer_id] === r.line_id;
                                        const showAddBtn = isLastLineForRetailer
                                          && ((perRetailerSum[r.retailer_id] || 0) < (retailerIndent[r.retailer_id] || 0) - 0.001);
                                        return (
                                          <tr key={r.line_id} className="border-t border-blue-100">
                                            <td className="p-2 text-gray-400 pl-6">{r.is_primary ? ridx + 1 : ''}</td>
                                            <td className="p-2 pl-2">
                                              {r.is_primary ? (
                                                <span className="inline-flex items-center gap-1.5">
                                                  {r.retailer_name}
                                                  {catOf(r) === 'gold' && (
                                                    <span
                                                      className="inline-flex items-center px-1.5 py-0.5 rounded text-[9px] font-semibold bg-yellow-100 text-yellow-800 border border-yellow-300"
                                                      title="Gold retailer - planned qty cannot be reduced below indent"
                                                      data-testid={`gold-chip-${r.retailer_id}`}
                                                    >
                                                      GOLD
                                                    </span>
                                                  )}
                                                  {parseNum(r.indent_qty) > 0 && (perRetailerSum[r.retailer_id] || 0) <= 0 && (
                                                    <span
                                                      className="inline-flex items-center px-1.5 py-0.5 rounded text-[9px] font-semibold bg-orange-100 text-orange-800 border border-orange-300"
                                                      title="Indent could not be allocated from pool - variant mismatch or stock exhausted. Pick a variant manually below."
                                                      data-testid={`stranded-chip-${r.retailer_id}`}
                                                    >
                                                      ⚠ UNALLOCATED
                                                    </span>
                                                  )}
                                                </span>
                                              ) : (
                                                <span className="italic text-gray-500">↳ same retailer</span>
                                              )}
                                            </td>
                                            <td className="p-2 text-center text-gray-700">
                                              {r.is_primary ? r.indent_qty : ''}
                                            </td>
                                            <td className="p-2 text-center">
                                              <BlankableNumberInput
                                                value={r.planned_qty}
                                                onChange={(v) => updateRetailerField(p.product_id, r.line_id, 'planned_qty', v)}
                                                className="w-20 h-7 px-1 text-center border rounded text-xs focus:outline-none focus:ring-1 focus:ring-emerald-500"
                                                testId={`retailer-planned-${p.product_id}-${r.line_id}`}
                                              />
                                            </td>
                                            <td className="p-2">
                                              <VariantCombobox
                                                value={r.variant}
                                                options={p.variant_options || []}
                                                recent={p.recent_variants || []}
                                                onChange={(v, opts) => updateRetailerField(p.product_id, r.line_id, 'variant', v, opts)}
                                                testId={`retailer-variant-${p.product_id}-${r.line_id}`}
                                              />
                                              {showAddBtn && (
                                                <button
                                                  type="button"
                                                  onClick={() => addAnotherVariant(p.product_id, r.retailer_id)}
                                                  className="mt-1 inline-flex items-center gap-1 text-[10px] text-emerald-700 hover:text-emerald-900 font-medium"
                                                  data-testid={`add-another-variant-${p.product_id}-${r.retailer_id}`}
                                                >
                                                  <Plus size={10} /> Add another variant
                                                </button>
                                              )}
                                            </td>
                                            <td className="p-2 text-center">
                                              <BlankableNumberInput
                                                value={r.mrp}
                                                onChange={(v) => updateRetailerField(p.product_id, r.line_id, 'mrp', v)}
                                                className="w-20 h-7 px-1 text-center border rounded text-xs focus:outline-none focus:ring-1 focus:ring-emerald-500"
                                                testId={`retailer-mrp-${p.product_id}-${r.line_id}`}
                                                step="0.5"
                                              />
                                            </td>
                                            <td className="p-2 text-center">
                                              {!r.is_primary && (
                                                <button
                                                  type="button"
                                                  onClick={() => removeRetailerLine(p.product_id, r.line_id)}
                                                  className="text-gray-400 hover:text-red-600"
                                                  title="Remove this line"
                                                  data-testid={`remove-line-${p.product_id}-${r.line_id}`}
                                                >
                                                  <Trash2 size={12} />
                                                </button>
                                              )}
                                            </td>
                                          </tr>
                                        );
                                      })}
                                    </tbody>
                                  </table>
                                </td>
                              </tr>
                            )}
                          </React.Fragment>
                        );
                      })}
                    </tbody>
                  </table>
                </div>
                )}
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
}
